import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Blobs, FloatingBubbles, PawPrint } from "../components/Effects";
import { RippleButton } from "../components/RippleButton";
import {
  Heart,
  ShieldCheck,
  Sparkles,
  Award,
  CheckCircle2,
  Home,
  Smile,
  ArrowRight,
  BadgeCheck,
  Lock,
} from "lucide-react";
import { useBooking } from "../context/BookingContext";

export default function OurPromise() {
  const navigate = useNavigate();
  const { openBookingModal } = useBooking();

  const handleBook = (serviceName = "Doorstep Grooming") => {
    openBookingModal(serviceName);
  };

  const promisePillars = [
    {
      icon: Heart,
      title: "Love, Patience & Respect",
      desc: "Every pet is treated like family. No rushing, no force — only gentle hands, warm care, and endless affection.",
      color: "from-rose-500/15 to-pink-500/5",
      borderColor: "border-rose-200",
      iconColor: "text-rose-500"
    },
    {
      icon: ShieldCheck,
      title: "100% Safety & Medical Hygiene",
      desc: "Medical-grade tool sterilization before every session, vet-approved organic products, and strict safety standards.",
      color: "from-emerald-500/15 to-teal-500/5",
      borderColor: "border-emerald-200",
      iconColor: "text-emerald-500"
    },
    {
      icon: Home,
      title: "Zero Stress & Cage-Free Comfort",
      desc: "No long car rides, no noisy salon cages. Your pet stays relaxed in the familiar warmth and safety of your home.",
      color: "from-sky-500/15 to-blue-500/5",
      borderColor: "border-sky-200",
      iconColor: "text-sky-500"
    },
    {
      icon: Award,
      title: "Uncompromised Trust & Quality",
      desc: "Certified, background-verified groomers with transparent fixed pricing and zero hidden charges, ever.",
      color: "from-amber-500/15 to-orange-500/5",
      borderColor: "border-amber-200",
      iconColor: "text-amber-500"
    }
  ];

  const checklistItems = [
    "100% Pet-Safe Organic & Toxin-Free Shampoos",
    "Sterilized Grooming Equipment Before Every Visit",
    "Background-Checked & Certified Grooming Experts",
    "Zero Cages, No Travel Anxiety, 100% Home Comfort",
    "Punctual Arrival & Real-Time Groomer Tracking",
    "Tailored Breed Care from First Brush to Final Cuddle"
  ];

  return (
    <div className="min-h-screen bg-[#FFF8F2] text-woofsy-dark overflow-hidden">
      <Navbar onBook={handleBook} />

      {/* Background Decorative Effects */}
      <Blobs />
      <FloatingBubbles count={12} />

      {/* Floating Paw Prints */}
      {[["6%", "15%", 0], ["90%", "20%", 1.2], ["80%", "70%", 2.4], ["8%", "75%", 3.6]].map(([l, t, d], i) => (
        <motion.div
          key={i}
          className="absolute text-woofsy-primary/15 w-8 h-8 pointer-events-none z-0"
          style={{ left: l, top: t }}
          animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, delay: d, repeat: Infinity, ease: "easeInOut" }}
        >
          <PawPrint className="w-8 h-8" />
        </motion.div>
      ))}

      {/* Hero Header Section */}
      <section className="relative pt-32 pb-12 px-5 sm:px-8 max-w-5xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs sm:text-sm font-semibold text-woofsy-dark shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-woofsy-primary animate-pulse" />
            Woofsy Pledge of Excellence • Pune, India
          </div>

          <h1 className="font-heading font-extrabold text-4xl sm:text-6xl lg:text-7xl leading-tight text-woofsy-dark">
            Our <span className="text-gradient">Promise</span>
          </h1>

          <p className="mt-3 text-xl sm:text-2xl font-heading font-bold text-woofsy-primary">
            Grooming. Care. Love. Delivered to Your Doorstep.
          </p>

          <p className="mt-4 text-base sm:text-lg text-woofsy-dark/75 max-w-2xl mx-auto leading-relaxed">
            Every pet deserves to be treated with love, patience, and respect.
          </p>
        </motion.div>
      </section>

      {/* Core Promise Card (Main Highlight Statement) */}
      <section className="py-8 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative glass rounded-[2.5rem] p-7 sm:p-11 shadow-2xl border-2 border-woofsy-primary/30 bg-gradient-to-br from-white via-[#FFF7F0] to-[#FFEFE2] overflow-hidden"
        >
          {/* Glowing Ambient Glow */}
          <div className="absolute -top-16 -left-16 w-52 h-52 bg-woofsy-primary/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -right-16 w-52 h-52 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 space-y-6 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center gap-4 border-b border-woofsy-primary/15 pb-5">
              <div className="w-14 h-14 rounded-2xl bg-woofsy-primary/15 flex items-center justify-center shrink-0 shadow-sm">
                <BadgeCheck className="w-8 h-8 text-woofsy-primary animate-pulse" />
              </div>
              <div>
                <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-woofsy-dark">
                  The Woofsy Care Guarantee
                </h2>
                <p className="text-xs sm:text-sm font-semibold text-woofsy-primary uppercase tracking-wider mt-0.5">
                  Our Uncompromising Quality Standard
                </p>
              </div>
            </div>

            <blockquote className="text-lg sm:text-2xl text-woofsy-dark font-medium italic leading-relaxed bg-woofsy-primary/10 p-5 rounded-2xl border-l-4 border-woofsy-primary shadow-sm">
              "Every pet deserves to be treated with love, patience, and respect. At Woofsy, we promise to deliver a safe, hygienic, and enjoyable grooming experience where your pet's comfort always comes first. From the first brush to the final cuddle, we're committed to making every grooming session a happy one."
            </blockquote>

            <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">🐾</span>
                <span className="font-heading font-bold text-lg text-woofsy-dark">
                  Woofsy – Grooming. Care. Love.
                </span>
              </div>

              <RippleButton variant="primary" className="px-6 py-3 text-sm font-semibold flex items-center gap-2" onClick={handleBook}>
                Book Doorstep Grooming <ArrowRight className="w-4 h-4" />
              </RippleButton>
            </div>
          </div>
        </motion.div>
      </section>

      {/* The 4 Pillars Section */}
      <section className="py-12 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-woofsy-primary">Pillars of Woofsy</span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-woofsy-dark mt-1">
              How We Fulfill Our Promise
            </h2>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {promisePillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                whileHover={{ y: -6, scale: 1.01 }}
                className={`glass rounded-3xl p-7 border ${p.borderColor} shadow-lg relative overflow-hidden bg-gradient-to-br ${p.color}`}
              >
                <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mb-4 shadow-sm">
                  <Icon className={`w-6 h-6 ${p.iconColor}`} />
                </div>

                <h3 className="font-heading font-bold text-xl text-woofsy-dark mb-2">
                  {p.title}
                </h3>

                <p className="text-sm sm:text-base text-woofsy-dark/80 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Interactive Promise Checklist */}
      <section className="py-12 px-5 sm:px-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass rounded-3xl p-8 sm:p-10 border border-white/80 shadow-xl bg-gradient-to-br from-white/90 via-white/70 to-sky-50/30"
        >
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-woofsy-primary">What You Can Expect</span>
            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-woofsy-dark mt-1">
              Every Session Guaranteed
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {checklistItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/80 border border-slate-100 shadow-sm"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <span className="text-sm font-semibold text-woofsy-dark">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-12 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass rounded-[2rem] p-7 sm:p-9 border-2 border-woofsy-primary/30 shadow-xl bg-gradient-to-r from-woofsy-primary/15 via-orange-500/10 to-amber-400/20 text-center space-y-4"
        >
          <div className="w-14 h-14 rounded-full bg-woofsy-primary text-white flex items-center justify-center mx-auto shadow-lg animate-pulse">
            <Sparkles className="w-7 h-7" />
          </div>

          <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-woofsy-dark">
            Give Your Pet the Doorstep Care They Deserve
          </h3>

          <p className="text-sm sm:text-base text-woofsy-dark/75 max-w-lg mx-auto">
            Experience stress-free, gentle grooming by certified professionals in Pune.
          </p>

          <RippleButton variant="primary" className="px-8 py-3.5 text-base font-bold shadow-lg" onClick={handleBook}>
            Book Grooming Session Now
          </RippleButton>
        </motion.div>
      </section>

      <Footer onBook={handleBook} />
    </div>
  );
}
